/**
 * @name: Kairaus Product Tabs
 * @author: Cursor (Agent)
 * @date: 2026-05-08
 * @description: Tabs + per-section Swiper init for product tab section sliders.
 * @dependencies: swiper-bundle.min.js, swiper-bundle.min.css
 * @strict-mode: true
 */

(() => {
  /**
   * @param {() => void} callback
   */
  const whenSwiperReady = (callback) => {
    if (typeof window.Swiper === 'function') {
      callback();
      return;
    }

    let attempts = 0;
    const tick = () => {
      if (typeof window.Swiper === 'function') {
        callback();
        return;
      }

      attempts += 1;
      if (attempts < 60) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  };

  const initTabs = (root) => {
    const tabs = Array.from(root.querySelectorAll('.tab'));
    const tabContents = Array.from(root.querySelectorAll('.tab-content'));
    if (!tabs.length) return;

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        for (const t of tabs) t.classList.remove('active');
        for (const c of tabContents) c.classList.remove('active');

        tab.classList.add('active');

        const targetId = tab.getAttribute('data-tab');
        if (!targetId) return;
        const target = root.querySelector(`#${CSS.escape(targetId)}`);
        target?.classList.add('active');
      });
    }
  };

  const initImageSwipers = (root) => {
    if (typeof window.Swiper !== 'function') return;

    /** @type {HTMLElement[]} */
    const sliders = Array.from(root.querySelectorAll('[data-kpt-image-swiper]'));
    if (!sliders.length) return;

    for (const slider of sliders) {
      if (slider.swiper) {
        slider.swiper.update();
        continue;
      }

      const area = slider.closest('.kpt-image-area');
      const paginationEl =
        slider.querySelector('[data-kpt-image-pagination]') ||
        slider.querySelector('.swiper-pagination') ||
        area?.querySelector('[data-kpt-image-pagination]');
      const slideEls = slider.querySelectorAll('.swiper-slide');
      if (slideEls.length < 2) continue;

      /** @type {any} */
      const config = {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        speed: 500,
        allowTouchMove: true,
        grabCursor: true,
        watchOverflow: true,
        nested: slider.closest('[data-kairaus-pdp-recommendations]') != null,
        // autoplay: {
        //   delay: 2500,
        //   disableOnInteraction: false,
        //   pauseOnMouseEnter: true,
        // },
      };

      if (paginationEl instanceof HTMLElement) {
        config.pagination = { el: paginationEl, clickable: true };
      }

      new window.Swiper(slider, config);
    }
  };

  const kptMoneyCache = new Map();

  const formatKptMoney = (cents, currency) => {
    const c = currency || 'USD';
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    const key = `${c}:${n}`;
    if (kptMoneyCache.has(key)) return kptMoneyCache.get(key);
    let out = '';
    try {
      out = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: c,
      }).format(n / 100);
    } catch {
      out = `₹${(n / 100).toFixed(2)}`;
    }
    kptMoneyCache.set(key, out);
    return out;
  };

  const bindSwatchButtons = (root) => {
    if (root.dataset.kptSwatchBound === 'true') return;
    root.dataset.kptSwatchBound = 'true';

    root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const sw = target.closest('[data-kpt-swatch-row] .pi-color-dot');
      if (!(sw instanceof HTMLButtonElement) || sw.disabled) return;

      const row = sw.closest('[data-kpt-swatch-row]');
      const card = sw.closest('[data-kpt-card]');
      if (!row || !card) return;

      event.preventDefault();

      for (const dot of row.querySelectorAll('.pi-color-dot')) {
        dot.classList.toggle('pi-color-dot--active', dot === sw);
        dot.setAttribute('aria-checked', dot === sw ? 'true' : 'false');
      }

      const variantId = sw.getAttribute('data-variant-id');
      const priceCents = sw.getAttribute('data-kpt-price-cents');
      const available = sw.getAttribute('data-kpt-available') === 'true';
      const gridRoot = card.closest(
        '[data-kpt-product-grid-root], [data-kairaus-pdp-recommendations], [data-kairaus-recently-viewed]'
      );
      const currency = gridRoot?.getAttribute('data-kpt-currency') || 'USD';

      const cartBtn = card.querySelector('.cart-btn');
      if (cartBtn instanceof HTMLButtonElement && variantId) {
        cartBtn.dataset.variantId = variantId;
        cartBtn.dataset.productId = variantId;
        cartBtn.disabled = !available;
      }

      const priceEl = card.querySelector('[data-kpt-price-display]');
      if (priceEl && priceCents) {
        priceEl.textContent = formatKptMoney(priceCents, currency);
      }

      const src = sw.getAttribute('data-kpt-media-src');
      const srcset = sw.getAttribute('data-kpt-media-srcset');
      if (src) {
        const imageSwiper = card.querySelector('[data-kpt-image-swiper]');
        if (imageSwiper?.swiper) {
          imageSwiper.swiper.slideTo(0);
        }
        const slideImg = card.querySelector(
          '.kpt-image-swiper .swiper-slide:first-child img, .image-wrapper > img'
        );
        if (slideImg instanceof HTMLImageElement) {
          slideImg.src = src;
          if (srcset) slideImg.srcset = srcset;
        }
      }
    });
  };

  const bindCartButtons = (root) => {
    const wrappers = [
      ...Array.from(root.querySelectorAll('.kairaus-product-tab-wrapper')),
      ...Array.from(root.querySelectorAll('[data-kairaus-pdp-recommendations]')),
      ...Array.from(root.querySelectorAll('[data-kairaus-recently-viewed]')),
    ];
    if (!wrappers.length) return;

    for (const wrapper of wrappers) {
      if (wrapper.dataset.kptCartBound === 'true') continue;
      wrapper.dataset.kptCartBound = 'true';

      wrapper.addEventListener('click', async (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('.cart-btn');
        if (!(button instanceof HTMLButtonElement)) return;

        event.preventDefault();

        const variantId = button.getAttribute('data-variant-id') || button.getAttribute('data-product-id');
        if (!variantId) return;
        if (button.getAttribute('aria-disabled') === 'true') return;

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        try {
          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            const message = data?.description || data?.message || 'Unable to add product to cart';
            throw new Error(message);
          }

          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

          if (theme?.settings?.afterAddToCart === 'drawer') {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
          }

          wrapper.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { variantId: data.variant_id || Number(variantId) },
          }));
        } catch (error) {
          wrapper.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: { error: error instanceof Error ? error.message : 'Cart error' },
          }));
        } finally {
          button.classList.remove('is-loading');
          button.classList.add('is-success');
          button.removeAttribute('aria-disabled');
          setTimeout(() => {
            button.classList.remove('is-success');
          }, 1200);
        }
      });
    }
  };

  const init = (root = document) => {
    const tabSections = Array.from(root.querySelectorAll('.kairaus-product-tab-wrapper'));
    const gridRoots = [
      ...tabSections,
      ...Array.from(root.querySelectorAll('[data-kairaus-pdp-recommendations]')),
      ...Array.from(root.querySelectorAll('[data-kairaus-recently-viewed]')),
      ...Array.from(root.querySelectorAll('[data-kairaus-recently-viewed-section]')),
    ];

    for (const section of tabSections) {
      initTabs(section);
    }

    whenSwiperReady(() => {
      for (const section of gridRoots) {
        initImageSwipers(section);
      }
    });

    for (const gridRoot of gridRoots) {
      bindSwatchButtons(gridRoot);
    }

    bindCartButtons(root);
  };

  document.addEventListener('DOMContentLoaded', () => init(document));

  document.addEventListener('kairaus:product-grid:init', (evt) => {
    const root = evt.detail?.root instanceof HTMLElement ? evt.detail.root : document;
    whenSwiperReady(() => {
      initImageSwipers(root);
      bindCartButtons(root);
    });

    if (root instanceof HTMLElement) {
      bindSwatchButtons(root);
    } else {
      for (const gridRoot of [
        ...Array.from(document.querySelectorAll('[data-kairaus-pdp-recommendations]')),
        ...Array.from(document.querySelectorAll('[data-kairaus-recently-viewed]')),
      ]) {
        bindSwatchButtons(gridRoot);
      }
    }
  });

  // Theme editor support
  document.addEventListener('shopify:section:load', (evt) => {
    const sectionRoot = evt.target;
    if (!(sectionRoot instanceof HTMLElement)) return;
    init(sectionRoot);
  });
})();

